'use client';

import { useState, useEffect } from 'react';
// import { useRouter } from 'next/navigation'; // Available for future navigation
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { Languages } from 'lucide-react';

interface LanguageOption {
  code: string;
  name: string;
  flag: string;
}

const LANGUAGES: LanguageOption[] = [
  { code: 'en-GB', name: 'English', flag: '🇬🇧' },
  { code: 'de-DE', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'de-AT', name: 'Deutsch (AT)', flag: '🇦🇹' },
  { code: 'de-CH', name: 'Deutsch (CH)', flag: '🇨🇭' },
  { code: 'en-IE', name: 'English (IE)', flag: '🇮🇪' }
];

interface PublicLanguageSwitcherProps {
  currentLocale?: string;
  className?: string;
}

export function PublicLanguageSwitcher({ 
  currentLocale = 'en-GB',
  className = '' 
}: PublicLanguageSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeLocale, setActiveLocale] = useState(currentLocale);
  // const router = useRouter(); // Available for future programmatic navigation

  // Load saved preference on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('preferred_language');
      if (saved) {
        setActiveLocale(saved);
      }
    }
  }, []);

  const currentLanguage = LANGUAGES.find(l => l.code === activeLocale) || LANGUAGES[0];

  const handleLanguageChange = (languageCode: string) => {
    // Store preference in cookie for middleware to read
    if (typeof window !== 'undefined') {
      document.cookie = `preferred_language=${languageCode}; path=/; max-age=31536000; SameSite=Lax`;
      localStorage.setItem('preferred_language', languageCode);
      
      // Reload page to apply new language
      window.location.reload();
    }
    setIsOpen(false);
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm"
          className={`gap-2 ${className}`}
          aria-label="Select language"
        >
          <Languages className="h-4 w-4" />
          <span className="hidden sm:inline">{currentLanguage.flag} {currentLanguage.name}</span>
          <span className="sm:hidden">{currentLanguage.flag}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {LANGUAGES.map((language) => (
          <DropdownMenuItem
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            className="flex items-center gap-3 cursor-pointer"
          >
            <span className="text-lg">{language.flag}</span>
            <span>{language.name}</span>
            {language.code === activeLocale && (
              <span className="ml-auto text-xs text-muted-foreground">✓</span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}